import { saveSession, type PlayerSession } from "./session";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Challenge {
  readonly code: string;
  readonly expiresAt: number;
  readonly resendAt: number;
  attempts: number;
}
const challenges = new Map<string, Challenge>();

export async function requestOtp(
  email: string,
  captchaToken?: string,
): Promise<{ expiresAt: number; resendAt: number; developmentCode?: string }> {
  const normalized = email.trim().toLowerCase();
  if (!EMAIL_RE.test(normalized)) throw new Error("请输入有效邮箱地址");
  const configuredEndpoint = (import.meta.env.VITE_GAME_API_URL ??
    import.meta.env.VITE_AUTH_API_URL) as string | undefined;
  if (configuredEndpoint) {
    const base = configuredEndpoint.replace(/\/$/u, "");
    const prefix = base.endsWith("/auth") ? base : `${base}/auth`;
    const response = await fetch(`${prefix}/otp/request`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        email: normalized,
        ...(captchaToken ? { captchaToken } : {}),
      }),
    });
    if (!response.ok) throw new Error("验证码发送失败，请稍后再试");
    const result = (await response.json()) as {
      expiresAt: number;
      resendAt: number;
    };
    if (import.meta.env.DEV) {
      const inbox = await fetch(
        `${base}/dev/inbox?email=${encodeURIComponent(normalized)}`,
      ).catch(() => null);
      if (inbox?.ok) {
        const body = (await inbox.json()) as { code?: unknown };
        if (typeof body.code === "string")
          return { ...result, developmentCode: body.code };
      }
    }
    return result;
  }
  const prior = challenges.get(normalized);
  if (prior && prior.resendAt > Date.now())
    throw new Error("验证码发送过于频繁，请稍后重试");
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  const now = Date.now();
  const challenge = {
    code: String((values[0] ?? 0) % 1_000_000).padStart(6, "0"),
    expiresAt: now + 5 * 60_000,
    resendAt: now + 60_000,
    attempts: 0,
  };
  challenges.set(normalized, challenge);
  return {
    expiresAt: challenge.expiresAt,
    resendAt: challenge.resendAt,
    developmentCode: challenge.code,
  };
}

export async function verifyOtp(email: string, code: string): Promise<PlayerSession> {
  const normalized = email.trim().toLowerCase();
  const configuredEndpoint = (import.meta.env.VITE_GAME_API_URL ??
    import.meta.env.VITE_AUTH_API_URL) as string | undefined;
  if (configuredEndpoint) {
    const base = configuredEndpoint.replace(/\/$/u, "");
    const prefix = base.endsWith("/auth") ? base : `${base}/auth`;
    const response = await fetch(`${prefix}/otp/verify`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email: normalized, code }),
    });
    if (!response.ok) throw new Error("验证码无效或已过期");
    const body = (await response.json()) as {
      sessionToken?: unknown;
      session?: { userId?: unknown; email?: unknown; expiresAt?: unknown };
    };
    if (
      typeof body.sessionToken !== "string" ||
      typeof body.session?.userId !== "string" ||
      typeof body.session.email !== "string" ||
      typeof body.session.expiresAt !== "number"
    ) {
      throw new Error("登录服务返回的会话无效");
    }
    const session: PlayerSession = {
      token: body.sessionToken,
      userId: body.session.userId,
      email: body.session.email,
      kind: "owner",
      expiresAt: body.session.expiresAt,
    };
    saveSession(session);
    return session;
  }
  const challenge = challenges.get(normalized);
  if (!challenge || challenge.expiresAt < Date.now())
    throw new Error("验证码已过期，请重新发送");
  challenge.attempts += 1;
  if (challenge.attempts > 6 || challenge.code !== code)
    throw new Error("验证码不正确");
  challenges.delete(normalized);
  const session: PlayerSession = {
    token: `local:${crypto.randomUUID()}`,
    userId: `local:${normalized}`,
    email: normalized,
    kind: "owner",
    expiresAt: Date.now() + 24 * 60 * 60_000,
  };
  saveSession(session);
  return session;
}
